import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { ReactElement, ReactNode } from 'react';

import { NUMERIC_FONT } from '../app/theme';

/** Font size of the discreet freshness line (px). */
const LINE_FONT_SIZE = 12;
/** Height of the line, kept when the activity mark comes and goes, so the screen never jumps. */
const LINE_MIN_HEIGHT = 18;
/** Size and stroke of the activity mark: smaller than the text it follows (M7 §4 "small"). */
const MARK_SIZE = 10;
const MARK_THICKNESS = 6;

// role=status, not MUI's default alert: the banner comes and goes with each 5 s refresh, and an
// assertive announcement at each change would drown the screen reader.
const STATUS_ROLE = 'status';

/**
 * Stands for the time in a translated sentence, so the time can be drawn in `NUMERIC_FONT` while
 * the sentence stays one catalogue text: pass it as the `{{time}}` value when translating
 * {@link FreshnessTexts}. U+2063 (invisible separator) never appears in a text.
 */
export const FRESHNESS_TIME_SLOT = '\u2063';

/**
 * The texts of the indicator, already translated (C3: the component knows no catalogue key).
 * `updatedAt`, `offline` and `failed` hold {@link FRESHNESS_TIME_SLOT} where the time goes.
 */
export interface FreshnessTexts {
  /** « À jour » / "Up to date". */
  readonly upToDate: string;
  /** « Mis à jour à {{time}} » / "Updated at {{time}}": data older than the freshness threshold. */
  readonly updatedAt: string;
  /** The Offline screen copy: no response, so no reason code (C2 §2). */
  readonly offline: string;
  /** Follows the reason text of a refused refresh: « Affichage du dernier état connu ({{time}}). » */
  readonly failed: string;
  /** Accessible name of the small activity mark. */
  readonly refreshing: string;
}

/**
 * Why the last refresh failed, while the data shown is the last known state (M7 R7). `offline`:
 * nothing answered (`ApiError.network`). `refused`: the API (or the session renewal) answered
 * with a reason; `reason` is its translated text (I12, C2), never a raw code.
 */
export type FreshnessFailure =
  { readonly kind: 'offline' } | { readonly kind: 'refused'; readonly reason: string };

/** Props of {@link FreshnessIndicator}. */
export interface FreshnessIndicatorProps {
  /** Time the server read the data (`generatedAt` / `refreshedAt`, C1), formatted HH:MM:SS. */
  readonly time: string;
  /**
   * The data arrived less than the freshness threshold ago (15 s, Q60), judged on the device
   * arrival time, not the server time (M7 R9). The caller computes it (`useIsFresh`).
   */
  readonly fresh: boolean;
  /** A background refresh is in flight (M7 R5). */
  readonly refreshing: boolean;
  /** Failure of the last refresh; `undefined` after a success. */
  readonly failure: FreshnessFailure | undefined;
  /** The texts, already translated by the caller (`useFreshness` builds them from the catalogue). */
  readonly texts: FreshnessTexts;
}

/**
 * How current the data on screen is (M7 §4, US-17 "never act on outdated information"):
 * "Up to date" when fresh, else "Updated at HH:MM:SS"; a small activity mark while a refresh runs;
 * after a failed refresh, an amber banner in its place (Offline, or the reason of the refusal),
 * with the time of the last known state and the same small mark while the next tick retries.
 * Times are drawn in `NUMERIC_FONT`.
 *
 * Shared by Home (E6) and Positions (E8), hence in `src/components` (C3 R1; the mockups draw
 * neither the line nor the banner, M7 §4: their placement is a proposal, to be validated). Texts
 * and the formatted time are props, so the component reads no catalogue and no API type; the
 * features build them with `useFreshness` (`src/features/shared/use-freshness.ts`).
 */
export function FreshnessIndicator({
  time,
  fresh,
  refreshing,
  failure,
  texts,
}: FreshnessIndicatorProps): ReactElement {
  const mark = refreshing ? <ActivityMark label={texts.refreshing} /> : undefined;
  if (failure !== undefined) {
    return (
      <Alert
        severity="warning"
        role={STATUS_ROLE}
        action={mark}
        sx={(theme) => ({ bgcolor: theme.fx.warningBg, color: theme.fx.warningText })}
      >
        {failure.kind === 'offline' ? (
          withTime(texts.offline, time)
        ) : (
          <>
            {failure.reason} {withTime(texts.failed, time)}
          </>
        )}
      </Alert>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.5,
        minHeight: LINE_MIN_HEIGHT,
        color: 'text.secondary',
        fontSize: LINE_FONT_SIZE,
      }}
    >
      <span>{fresh ? texts.upToDate : withTime(texts.updatedAt, time)}</span>
      {mark}
    </Box>
  );
}

/** The small activity mark of a refresh in flight (M7 §4 "small"), named for screen readers. */
function ActivityMark({ label }: { readonly label: string }): ReactElement {
  return <CircularProgress size={MARK_SIZE} thickness={MARK_THICKNESS} aria-label={label} />;
}

/**
 * Puts `time`, in `NUMERIC_FONT`, where {@link FRESHNESS_TIME_SLOT} stands in `sentence`. A
 * sentence without exactly one slot (a translation that drops or repeats `{{time}}`; a catalogue
 * test guards it) falls back to plain text with every slot replaced by the time and the spaces
 * tidied, so no invisible separator or doubled space ever reaches the screen.
 */
function withTime(sentence: string, time: string): ReactNode {
  const parts = sentence.split(FRESHNESS_TIME_SLOT);
  if (parts.length !== 2) {
    return sentence.replaceAll(FRESHNESS_TIME_SLOT, time).replace(/\s+/g, ' ').trim();
  }
  const [before = '', after = ''] = parts;
  return (
    <>
      {before}
      <Box component="span" sx={{ fontFamily: NUMERIC_FONT }}>
        {time}
      </Box>
      {after}
    </>
  );
}
